import React, { useEffect, useRef } from "react";

// Fixed CRT scanline + noise overlays (pointer-events none)
export function ScreenEffects() {
  return (
    <>
      <div className="mizu-scanlines fixed inset-0 z-[60] opacity-60" aria-hidden />
      <div className="mizu-noise fixed inset-0 z-[60]" aria-hidden />
    </>
  );
}

// Subtle animated grid backdrop
export function GridBackground({ className = "" }) {
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 mizu-grid animate-grid-drift ${className}`}
      style={{
        maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
      }}
    />
  );
}

// Lightweight canvas particles — terminal page only.
export function Particles({ count = 40, className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const dots = Array.from({ length: count }, () => ({
      x: Math.random(),
      y: Math.random(),
      s: 0.3 + Math.random() * 0.9,
      v: 0.00012 + Math.random() * 0.0004,
      o: 0.15 + Math.random() * 0.45,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of dots) {
        p.y -= p.v;
        if (p.y < -0.02) {
          p.y = 1.02;
          p.x = Math.random();
        }
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.s, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0, 240, 255, ${p.o})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [count]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${className}`}
    />
  );
}

// Thin animated data-stream column (decorative)
export function DataStream({ left, delay = 0, duration = 9 }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute top-0 h-full w-px overflow-hidden"
      style={{ left }}
    >
      <div
        className="h-24 w-px bg-gradient-to-b from-transparent via-mizu-cyan/50 to-transparent"
        style={{
          animation: `data-stream ${duration}s linear infinite`,
          animationDelay: `${delay}s`,
        }}
      />
    </div>
  );
}

// Section eyebrow label: "> LABEL"
export function TermLabel({ children, className = "" }) {
  return (
    <div
      className={`font-mono text-xs tracking-[0.25em] text-mizu-cyan/90 mizu-glow ${className}`}
    >
      {children}
    </div>
  );
}

export default ScreenEffects;
